import { faChevronCircleDown } from "@fortawesome/pro-solid-svg-icons/faChevronCircleDown";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons/faCircleCheck";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons/faCircleExclamation";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons/faTriangleExclamation";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons/faCircleInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { Components, Theme } from "@mui/material/styles";
import { paletteOptions } from "./palette";

const MuiAccordion: Components<Theme>["MuiAccordion"] = {
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      // Not a theme color so that even on non-white backgrounds,
      // the border stands out
      border: "1px solid rgba(0,0,0,0.2)",
      "&.Mui-expanded": {
        borderColor: theme.palette.primary.main,
      },
      ...(!!(ownerState.onClick || ownerState.onChange) &&
        !ownerState.expanded && {
          cursor: "pointer",
        }),
    }),
  },
};

const MuiAccordionDetails: Components<Theme>["MuiAccordionDetails"] = {
  styleOverrides: {
    root: {
      padding: "1rem",
    },
  },
};

const MuiAccordionSummary: Components<Theme>["MuiAccordionSummary"] = {
  defaultProps: {
    expandIcon: <FontAwesomeIcon icon={faChevronCircleDown} />,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      "&.Mui-expanded": {
        borderBottom: `1px solid ${theme.palette.dream.grayLight}`,
      },
    }),
    content: {
      "&.Mui-expanded": {
        margin: 0,
        fontWeight: "bold",
      },
    },
    expandIconWrapper: {
      padding: "1rem",
      margin: "-1.3rem -1rem -1.3rem 0",
    },
  },
};

const MuiAlert: Components<Theme>["MuiAlert"] = {
  defaultProps: {
    variant: "outlined",
    iconMapping: {
      success: <FontAwesomeIcon icon={faCircleCheck} />,
      info: <FontAwesomeIcon icon={faCircleInfo} />,
      warning: <FontAwesomeIcon icon={faTriangleExclamation} />,
      error: <FontAwesomeIcon icon={faCircleExclamation} />,
    },
  },
  styleOverrides: {
    root: {
      /*
      This resets the font size of the alert body; the default is that the
      font size is a bit smaller, which is awkward.
       */
      fontSize: "1rem",
    },
    outlined: {
      color: "unset",
    },
    outlinedError: ({ theme }) => ({
      borderColor: theme.palette.error.main,
      "& .MuiAlert-icon": {
        color: theme.palette.error.main,
      },
    }),
    outlinedSuccess: ({ theme }) => ({
      borderColor: theme.palette.success.main,
      "& .MuiAlert-icon": {
        color: theme.palette.success.main,
      },
    }),
  },
};

const MuiAlertTitle: Components<Theme>["MuiAlertTitle"] = {
  defaultProps: {
    sx: { mb: 1 },
  },
  styleOverrides: {
    root: {
      /*
      This sets the font size of the alert title.
       */
      fontSize: "1.1rem",
      fontWeight: 600,
    },
  },
};

const MuiAutocomplete: Components<Theme>["MuiAutocomplete"] = {
  styleOverrides: {
    root: {
      // Fixes collision with p margin styling
      "& .MuiOutlinedInput-root": {
        padding: 7.5, // Reduced from 9
      },
    },
  },
};

const MuiButton: Components<Theme>["MuiButton"] = {
  defaultProps: {
    variant: "contained",
    disableRipple: true,
  },
  styleOverrides: {
    root: {
      textTransform: "none",
      fontSize: "1rem",
      lineHeight: 1.45,
      "& > p": {
        margin: 0,
      },
      "&.MuiButton-text:hover": {
        textDecoration: "underline",
      },
      "&:not(.MuiButton-text)": {
        height: "3rem",
        padding: "0.375rem 1rem",
        "&:not(.MuiButtonGroup-grouped)": {
          borderRadius: ".5rem",
        },
      },
      "&.Mui-disabled": {
        cursor: "not-allowed",
      },
    },

    /**
     * <Button /> // (Defaults to variant="contained")
     */
    contained: {
      color: "white",
      "&.Mui-disabled": {
        color: "white",
        backgroundColor: "#A8D4FF",
      },
    },
    containedPrimary: ({ theme }) => ({
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    }),
    containedSecondary: ({ theme }) => ({
      backgroundColor: theme.palette.dream.grayMedium,
      color: theme.palette.primary.main,
      "&:active": {
        backgroundColor: theme.palette.dream.grayMediumDark,
        color: theme.palette.common.white,
      },
      "@media (hover: hover)": {
        "&:hover": {
          color: "white",
        },
      },
    }),

    /**
     * <Button variant="outlined" />
     */
    outlined: ({ theme }) => ({
      color: theme.palette.text.primary,
      "&:active": {
        outline: "1px solid rgba(0,0,0,0.1)",
      },
      "&.Mui-disabled": {
        color: "#A8D4FF",
        border: `1px solid #A8D4FF`,
      },
    }),
    outlinedPrimary: ({ theme }) => ({
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
      "&:active": {
        color: theme.palette.primary.dark,
        border: `1px solid ${theme.palette.primary.dark}`,
      },
    }),
    outlinedSecondary: ({ theme }) => ({
      color: theme.palette.dream.grayMediumDark,
      border: `1px solid ${theme.palette.dream.grayMediumDark}`,
      "&:active": {
        color: theme.palette.dream.grayDark,
        border: `1px solid ${theme.palette.dream.grayDark}`,
      },
      "@media (hover: hover)": {
        "&:hover": {
          borderColor: theme.palette.dream.grayMediumDark,
          background: "rgba(207, 203, 222, 0.14)",
        },
      },
    }),
    outlinedSuccess: ({ theme }) => ({
      color: theme.palette.success.main,
      border: `1px solid ${theme.palette.success.main}`,
      "&:active": {
        color: theme.palette.success.dark,
        border: `1px solid ${theme.palette.success.dark}`,
      },
    }),
    outlinedInfo: ({ theme }) => ({
      color: theme.palette.info.main,
      border: `1px solid ${theme.palette.info.main}`,
      "&:active": {
        color: theme.palette.info.dark,
        border: `1px solid ${theme.palette.info.dark}`,
      },
    }),
    outlinedWarning: ({ theme }) => ({
      color: theme.palette.warning.main,
      border: `1px solid ${theme.palette.warning.main}`,
      "&:active": {
        color: theme.palette.warning.dark,
        border: `1px solid ${theme.palette.warning.dark}`,
      },
    }),
    outlinedError: ({ theme }) => ({
      color: theme.palette.error.main,
      border: `1px solid ${theme.palette.error.main}`,
      "&:active": {
        color: theme.palette.error.dark,
        border: `1px solid ${theme.palette.error.dark}`,
      },
    }),

    /**
     * <Button variant="text" />
     */
    text: {
      boxShadow: "none",
      backgroundColor: "transparent",
      "&:hover, &:active": {
        backgroundColor: "transparent",
      },
      "&.Mui-disabled": {
        color: "#A8D4FF",
      },
    },
    textPrimary: ({ theme }) => ({
      "&:active": {
        color: theme.palette.primary.dark,
      },
    }),
    textSecondary: ({ theme }) => ({
      "&:active": {
        color: theme.palette.secondary.dark,
      },
    }),
    textSuccess: ({ theme }) => ({
      "&:active": {
        color: theme.palette.success.dark,
      },
    }),
    textInfo: ({ theme }) => ({
      "&:active": {
        color: theme.palette.info.dark,
      },
    }),
    textWarning: ({ theme }) => ({
      "&:active": {
        color: theme.palette.warning.dark,
      },
    }),
    textError: ({ theme }) => ({
      "&:active": {
        color: theme.palette.error.dark,
      },
    }),
  },
  variants: [
    {
      // Program-card call to action (funnel result card, hosted Get Info,
      // dashboard and storefront cards): green, bold, a step larger.
      props: { variant: "cta" },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.dream.ctaBackground,
        color: theme.palette.common.white,
        fontSize: "1.125rem",
        fontWeight: 700,
        "@media (hover: hover)": {
          "&:hover": {
            backgroundColor: theme.palette.dream.ctaBackgroundHover,
          },
        },
        "&.Mui-disabled": {
          color: theme.palette.common.white,
          backgroundColor: "#86EFAC",
        },
        // ButtonBase zeroes the outline and only `contained` ships a focus
        // shadow, so a custom variant has to draw its own keyboard focus ring.
        "&.Mui-focusVisible, &:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }),
    },
    {
      props: { variant: "onDarkOutlined" },
      style: ({ theme }) => ({
        color: theme.palette.common.white,
        background: "transparent",
        border: `1px solid ${theme.palette.common.white}`,
        "&:hover, &:active, &:focus-visible": {
          color: theme.palette.common.white,
          background: "rgba(255, 255, 255, 0.08)",
          borderColor: theme.palette.common.white,
        },
        "&.Mui-disabled": {
          color: theme.palette.common.white,
          borderColor: theme.palette.common.white,
        },
      }),
    },
    {
      props: { variant: "onDark" },
      style: ({ theme }) => ({
        background: theme.palette.common.white,
        border: "1px solid #D1D5DC",
        "&:hover, &:active": {
          background: theme.palette.common.white,
        },
        "&.Mui-disabled": {
          color: "#A8D4FF",
          border: "1px solid #A8D4FF",
        },
      }),
    },
    {
      props: { variant: "onDark", color: "primary" },
      style: ({ theme }) => ({
        background: theme.palette.common.white,
        borderColor: "#D1D5DC",
        color: theme.palette.text.primary,
        "&:hover, &:active": {
          borderColor: theme.palette.primary.main,
        },
      }),
    },
    {
      props: { variant: "onDark", color: "secondary" },
      style: ({ theme }) => ({
        color: theme.palette.dream.grayMediumDark,
        "&:hover, &:active": {
          borderColor: theme.palette.primary.main,
        },
      }),
    },
    {
      props: { variant: "onDark", color: "info" },
      style: ({ theme }) => ({
        borderColor: theme.palette.info.light,
        color: theme.palette.info.dark,
        "&:hover, &:active": {
          borderColor: theme.palette.info.dark,
        },
      }),
    },
    {
      props: { variant: "onDark", color: "warning" },
      style: ({ theme }) => ({
        borderColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        "&:hover, &:active": {
          borderColor: theme.palette.warning.dark,
        },
      }),
    },
    {
      props: { variant: "onDark", color: "success" },
      style: ({ theme }) => ({
        borderColor: theme.palette.success.light,
        color: theme.palette.success.main,
        "&:hover, &:active": {
          borderColor: theme.palette.success.dark,
        },
      }),
    },
    {
      props: { variant: "onDark", color: "error" },
      style: ({ theme }) => ({
        borderColor: theme.palette.error.light,
        color: theme.palette.error.main,
        "&:hover, &:active": {
          borderColor: theme.palette.error.dark,
        },
      }),
    },
  ],
};

const MuiButtonBase: Components<Theme>["MuiButtonBase"] = {
  defaultProps: {
    disableRipple: true,
  },
};

const MuiCard: Components<Theme>["MuiCard"] = {
  defaultProps: {
    variant: "outlined",
  },
};

const MuiCardContent: Components<Theme>["MuiCardContent"] = {
  styleOverrides: {
    root: {
      "& > *:first-child": {
        marginTop: 0,
      },
    },
  },
};

const MuiCheckbox: Components<Theme>["MuiCheckbox"] = {
  defaultProps: { disableFocusRipple: true, disableTouchRipple: true },
};

const MuiChip: Components<Theme>["MuiChip"] = {
  styleOverrides: {
    root: {
      "& .MuiChip-icon": {
        paddingLeft: "0.35em",
        fontSize: "1em",
      },
    },
    sizeSmall: {
      fontSize: "0.6rem",
      height: "1rem",
      borderRadius: "0.75rem",
      "& .MuiChip-label": {
        paddingLeft: "0.6rem",
        paddingRight: "0.6rem",
      },
    },
  },
};

const MuiDialog: Components<Theme>["MuiDialog"] = {
  styleOverrides: {
    root: {
      "& .MuiBackdrop-root": {
        // This applies a blur to Backdrop for Dialog only
        backdropFilter: "blur(5px)",
      },
    },
  },
};

const MuiDialogActions: Components<Theme>["MuiDialogActions"] = {
  defaultProps: { sx: { p: 2, pt: 0 } },
};

const MuiDialogContent: Components<Theme>["MuiDialogContent"] = {
  defaultProps: { sx: { px: 4, py: 3 } },
  styleOverrides: {
    root: {
      "& > *:last-child": {
        marginBottom: 0,
      },
    },
  },
};

const MuiDialogTitle: Components<Theme>["MuiDialogTitle"] = {
  styleOverrides: {
    root: {
      // Copied from <Typography variant="new_h2">
      fontSize: "1.25rem",
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: "-0.01rem",
    },
  },
  defaultProps: {
    sx: { p: 4, pb: 2 },
  },
};

const MuiIconButton: Components<Theme>["MuiIconButton"] = {
  styleOverrides: {
    root: {
      "&.MuiIconButton-sizeMedium": {
        width: 40,
      },
    },
  },
};

const MuiInputLabel: Components<Theme>["MuiInputLabel"] = {
  defaultProps: {
    disableAnimation: true,
    shrink: true,
  },
};

const MuiOutlinedInput: Components<Theme>["MuiOutlinedInput"] = {
  defaultProps: {
    notched: true,
  },
};

const MuiPaper: Components<Theme>["MuiPaper"] = {
  styleOverrides: {
    root: {
      borderColor: "#D1D5DC",
      boxShadow: "0px 2px 2px 0px rgba(209, 213, 220, 0.2)",
    },
  },
  defaultProps: {
    variant: "outlined",
  },
  /**
   * DO NOT ADD padding or margin to default variant! Mui Paper is used by a
   * ton of internal components and this will break things. -BW, 2023/03/28
   *
   * Number of times this mistake has been made: 4
   */
  variants: [
    {
      props: { variant: "paddedOutlined" },
      style: {
        padding: "1rem",
      },
    },
  ],
};

const MuiRadio: Components<Theme>["MuiRadio"] = {
  defaultProps: {
    disableRipple: true,
  },
};

const MuiSelect: Components<Theme>["MuiSelect"] = {
  defaultProps: {
    variant: "standard",
  },
};

const MuiSkeleton: Components<Theme>["MuiSkeleton"] = {
  styleOverrides: {
    rounded: {
      borderRadius: "0.5rem",
    },
  },
};

const MuiTableCell: Components<Theme>["MuiTableCell"] = {
  styleOverrides: {
    root: {
      fontSize: "1rem",
    },
    head: {
      fontWeight: "bold",
    },
  },
};

const MuiTablePagination: Components<Theme>["MuiTablePagination"] = {
  styleOverrides: {
    root: {
      // Fixes collision with p margin styling
      "& .MuiTablePagination-caption": {
        margin: 0,
      },
    },
  },
};

const MuiTypography: Components<Theme>["MuiTypography"] = {
  defaultProps: {
    variantMapping: {
      page_title: "h1",
      new_h2: "h2",
      new_h3: "h3",
      disclaimer: "p",
      overline: "div",
      label: "p",
    },
  },
  styleOverrides: {
    root: {
      color: paletteOptions.text.primary,
    },
    overline: {
      margin: "0 0 0.2rem 0",
      color: paletteOptions.primary.main,
    },
    paragraph: {
      margin: "0.8rem 0 0 0",
      maxWidth: "50em",
    },
    page_title: {
      margin: "0 0 1rem 0",
    },
    new_h2: {
      margin: "1.5rem 0 1rem 0",
    },
    new_h3: {
      margin: "1.5rem 0 1rem 0",
    },
  },
};

export const components: Components<Theme> = {
  MuiAccordion,
  MuiAccordionDetails,
  MuiAccordionSummary,
  MuiAlert,
  MuiAlertTitle,
  MuiAutocomplete,
  MuiButton,
  MuiButtonBase,
  MuiCard,
  MuiCardContent,
  MuiCheckbox,
  MuiChip,
  MuiDialog,
  MuiDialogActions,
  MuiDialogContent,
  MuiDialogTitle,
  MuiIconButton,
  MuiInputLabel,
  MuiOutlinedInput,
  MuiPaper,
  MuiRadio,
  MuiSelect,
  MuiSkeleton,
  MuiTableCell,
  MuiTablePagination,
  MuiTypography,
};
